import {
  Box,
  Chip,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  InputAdornment,
  IconButton,
  Badge,
} from '@mui/material';
import {
  type DispatchPlanIds,
  type ExtendedDispatchPlanHistoryResponseItemType,
} from '../DispatchPlanHistory';
import { useMemo, useState, useEffect } from 'react';
import {
  RemoveRedEye,
  KeyboardArrowDown,
  Close,
  Circle,
} from '@mui/icons-material';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import useCustomerInfo from 'hooks/useCustomerInfo';
import dayjs, { type Dayjs } from 'dayjs';
import { PreviewDateSelectorModal } from '../modal/PreviewDateSelectorModal';
import {
  PickersDay,
  type PickersDayProps,
} from '@mui/x-date-pickers/PickersDay';
import {
  type BaseSingleInputFieldProps,
  CalendarIcon,
  DatePicker,
  type DatePickerProps,
  type DateValidationError,
  type FieldSection,
  type UseDateFieldProps,
} from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import useAppTheme from 'hooks/useAppTheme';

const timelineColor = '#6495ED';

interface ButtonFieldProps
  extends UseDateFieldProps<Dayjs>,
    BaseSingleInputFieldProps<
      Dayjs | null,
      Dayjs,
      FieldSection,
      DateValidationError
    > {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const DayComponent = (
  props: PickersDayProps<Dayjs> & { highlightedDays?: Set<string> }
) => {
  const {
    highlightedDays = new Set(),
    day,
    outsideCurrentMonth,
    ...other
  } = props;

  const dayString = day.format('YYYY-MM-DD');
  const hasPlan = highlightedDays.has(dayString);
  const isSelected = other.selected;
  const showBadge = !outsideCurrentMonth && hasPlan;

  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <Badge
      key={props.day.toString()}
      overlap="circular"
      badgeContent={
        showBadge ? (
          <Circle
            color="success"
            sx={{
              fontSize: '8px',
            }}
          />
        ) : undefined
      }
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        sx={{
          fontSize: '0.8rem',

          // margin: '0 2px',
          ...(isSelected && {
            backgroundColor: `${timelineColor} !important`,
            color: '#fff',
            fontWeight: 'bold',
          }),
          ...(!isSelected &&
            hasPlan && {
              border: `1px solid ${alpha(timelineColor, 0.3)}`,
            }),
        }}
      />
    </Badge>
  );
};

const CalendarIconButton = (props: ButtonFieldProps) => {
  const {
    setOpen,
    id,
    disabled,
    InputProps: { ref } = {},
    inputProps: { 'aria-label': ariaLabel } = {},
  } = props;
  const { customerTz } = useCustomerInfo();

  return (
    <IconButton
      id={id}
      disabled={disabled}
      ref={ref}
      aria-label={ariaLabel}
      onClick={() => setOpen?.((prev) => !prev)}
      size="small"
      sx={{
        border: '1px solid',
        borderColor: 'borderColor.grey',
        p: 1.4,
        borderRadius: 2,
        fontSize: '0.8rem',
      }}
    >
      {props.value ? (
        convertUtcToDisplayTime(
          props.value.toDate(),
          'configured',
          customerTz,
          DATE_FORMAT
        )
      ) : (
        <CalendarIcon fontSize="small" />
      )}
    </IconButton>
  );
};

const CalendarButtonField = (
  props: Omit<DatePickerProps<Dayjs>, 'open' | 'onOpen' | 'onClose'> & {
    activeDatesSet: Set<string>;
  }
) => {
  const [open, setOpen] = useState(false);
  const { customerTz } = useCustomerInfo();

  return (
    <DatePicker
      slots={{
        field: CalendarIconButton,
        day: DayComponent,
        ...props.slots,
      }}
      slotProps={{
        field: { setOpen } as any,
        actionBar: {
          actions: ['clear', 'today'],
        },
        shortcuts: {
          changeImportance: 'set',
        },
        day: {
          highlightedDays: props.activeDatesSet,
        } as any,
      }}
      {...props}
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      onOpen={() => {
        setOpen(true);
      }}
      timezone={customerTz}
      disableFuture
      closeOnSelect={false}
    />
  );
};

export const HistoryList = ({
  selectedDateRange,
  date,
  historyData,
  activePlan,
  selectedPlan,
  handleViewPlanDetails,
  handleSetDispatchSummaryPlan,
}: {
  selectedDateRange: string;
  date: dayjs.Dayjs | null;
  historyData: ExtendedDispatchPlanHistoryResponseItemType[];
  activePlan: DispatchPlanIds;
  selectedPlan: ExtendedDispatchPlanHistoryResponseItemType | null;

  handleViewPlanDetails: (
    historyDataItem: ExtendedDispatchPlanHistoryResponseItemType
  ) => void;

  handleSetDispatchSummaryPlan: (
    historyDataItem: ExtendedDispatchPlanHistoryResponseItemType,
    isActive: boolean,
    setActivePlan: boolean,
    paramSelectedRange: string,
    paramSelectedDate: Dayjs | null
  ) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { customerTz } = useCustomerInfo();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCalendarDate, setSelectedCalendarDate] =
    useState<Dayjs | null>(null);

  const [modalData, setModalData] = useState<{
    open: boolean;
    plan: null | ExtendedDispatchPlanHistoryResponseItemType;
    isActive: boolean;
  }>({ open: false, plan: null, isActive: false });

  const { activeDatesSet, latestDate } = useMemo(() => {
    const dates = new Set<string>();
    let maxDate: Dayjs | null = null;

    historyData?.forEach((plan) => {
      if (plan.startDateWithTime) {
        const planDate = dayjs(plan.startDateWithTime).tz(customerTz);
        dates.add(planDate.format('YYYY-MM-DD'));

        if (!maxDate || planDate.isAfter(maxDate)) {
          maxDate = planDate;
        }
      }
    });

    return { activeDatesSet: dates, latestDate: maxDate };
  }, [historyData, customerTz]);

  useEffect(() => {
    if (latestDate) {
      setSelectedCalendarDate(latestDate);
    }
  }, [latestDate]);

  const filteredPlans = useMemo(() => {
    // If Search is active, ignore Calendar Date
    if (searchTerm.trim().length > 0) {
      return historyData?.filter((plan) =>
        plan?.planName?.toLowerCase()?.includes(searchTerm.toLowerCase())
      );
    }

    //  If Search is empty, use Calendar Date
    if (selectedCalendarDate) {
      return historyData?.filter((plan) => {
        if (!plan.startDateWithTime) return false;
        const planDate = dayjs(plan.startDateWithTime).tz(customerTz);
        return planDate.isSame(selectedCalendarDate, 'day');
      });
    }

    return historyData;
  }, [searchTerm, historyData, selectedCalendarDate, customerTz]);

  // fr timeline view
  const groupedPlans = useMemo(() => {
    const groups: Record<string, typeof filteredPlans> = {};

    filteredPlans.forEach((plan) => {
      let dateKey = 'Unknown Date';
      if (plan.startDateWithTime) {
        try {
          dateKey = convertUtcToDisplayTime(
            plan.startDateWithTime,
            'configured',
            customerTz,
            'ddd, MMM D, YYYY'
          );
        } catch (e) {
          console.error('Date parsing error', e);
        }
      }

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(plan);
    });

    return groups;
  }, [filteredPlans, customerTz]);

  const handleCalendarChange = (newValue: Dayjs | null) => {
    setSelectedCalendarDate(newValue);
    setSearchTerm('');
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    if (latestDate) {
      setSelectedCalendarDate(latestDate);
    }
  };

  const handleClosePreviewDateSelectorModal = () => {
    setModalData({ open: false, plan: null, isActive: false });
  };

  const handleApply = async (
    paramSelectedRange: string,
    paramSelectedDate: Dayjs
  ) => {
    try {
      if (modalData.open && modalData.plan) {
        handleClosePreviewDateSelectorModal();
        await handleSetDispatchSummaryPlan(
          modalData.plan,
          modalData.isActive,
          false,
          paramSelectedRange,
          paramSelectedDate
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box
      sx={{
        width: '26%',
        borderRight: '1px solid',
        borderColor: 'borderColor.grey',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'borderColor.grey',
        }}
        display={'flex'}
        alignItems={'center'}
        gap={2}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          minWidth={'fit-content'}
        >
          {t('plans')}
          {filteredPlans.length})
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          width={'100%'}
          gap={1}
          justifyContent={'space-between'}
        >
          <TextField
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (selectedCalendarDate) setSelectedCalendarDate(null);
            }}
            variant="outlined"
            size="small"
            placeholder={t('search_plans')}
            InputProps={{
              style: { borderRadius: '8px' },
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    size="small"
                    edge="end"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            fullWidth
          />
          <CalendarButtonField
            value={selectedCalendarDate}
            onChange={(newValue) => {
              handleCalendarChange(newValue);
            }}
            activeDatesSet={activeDatesSet}
          />
        </Box>
      </Box>

      {/* {searchTerm.length <= 0 ? (
        <Box
          sx={{
            p: 1,
          }}
        >
          <Paper
            sx={{
              border: '1px solid',
              borderColor: 'borderColor.grey',
              borderRadius: 2,
            }}
            elevation={0}
          >
            <HistoryCalendar
              selectedDate={selectedCalendarDate}
              onDateChange={handleCalendarChange}
              activeDatesSet={activeDatesSet}
              isDisabled={searchTerm.length > 0}
            />
          </Paper>
        </Box>
      ) : null} */}

      <Box
        sx={{
          overflowY: 'auto',
          flex: 1,
          px: 1,
          py: 2,
        }}
        display={'flex'}
        flexDirection={'column'}
        gap={0}
      >
        {filteredPlans.length === 0 && (
          <Box
            textAlign="center"
            mt={4}
            p={3}
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              mx: 2,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              title={searchTerm ?? ''}
            >
              {searchTerm
                ? `No plans found matching "${searchTerm}"`
                : `No plans found for ${
                    selectedCalendarDate
                      ? convertUtcToDisplayTime(
                          selectedCalendarDate?.toDate(),
                          'configured',
                          customerTz,
                          DATE_FORMAT
                        )
                      : ''
                  }`}
            </Typography>
            {!searchTerm && (
              <Typography variant="caption" color="text.disabled">
                {t('try_selecting_a_date_with_a_blue_outline')}
              </Typography>
            )}
          </Box>
        )}
        {Object.entries(groupedPlans).map(
          ([dateLabel, plans], groupIndex, arr) => {
            return (
              <Box
                key={dateLabel}
                sx={{
                  position: 'relative',
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: (theme) =>
                    alpha(
                      timelineColor,
                      theme.palette.mode === 'light' ? 0.05 : 0.12
                    ),
                  border: '1px solid',
                  borderColor: (theme) => alpha(timelineColor, 0.15),
                }}
              >
                <Accordion
                  defaultExpanded
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: 'transparent',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <KeyboardArrowDown sx={{ color: timelineColor }} />
                    }
                    aria-controls={`panel-${dateLabel}-content`}
                    id={`panel-${dateLabel}-header`}
                    sx={{
                      padding: 0,
                      minHeight: 'auto',
                      '& .MuiAccordionSummary-content': {
                        margin: 0,
                        alignItems: 'center',
                        gap: 1,
                      },
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        marginRight: 1,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: '3px solid',
                        borderColor: 'background.paper',
                        backgroundColor: timelineColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                        flexShrink: 0,
                        zIndex: 1,
                      }}
                    />

                    <Typography variant="subtitle2" fontWeight="bold">
                      {dateLabel}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ padding: 0, pt: 1, pb: 0 }}>
                    <Box
                      sx={{
                        ml: '9px',
                        pl: 3,
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={2}>
                        {plans.map((plan, index) => {
                          const isLast = index === plans.length - 1;
                          const isActive =
                            plan.historyId === activePlan.historyId;
                          const isSelected =
                            selectedPlan?.historyId === plan.historyId;
                          const blendRatios = plan.blendRatio?.D0 || [];
                          const loadingAreas = ['L0', 'L1'];
                          const blendString =
                            blendRatios.length > 0
                              ? loadingAreas
                                  .slice(0, blendRatios.length)
                                  .map(
                                    (area, idx) =>
                                      `${area}: ${
                                        (blendRatios[idx] || 0) * 100
                                      }%`
                                  )
                                  .join(' / ')
                              : 'N/A';

                          return (
                            <Box
                              key={plan.historyId}
                              sx={{
                                position: 'relative',
                                padding: 2,
                                border: isSelected ? '1px dashed' : '1px solid',
                                borderColor: isSelected
                                  ? timelineColor
                                  : 'divider',
                                backgroundColor: isSelected
                                  ? 'background.paper'
                                  : (theme) => alpha(timelineColor, 0.08),
                                borderRadius: 2,
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: isSelected
                                    ? timelineColor
                                    : (theme) => alpha(timelineColor, 0.5),
                                  boxShadow: 1,
                                },
                              }}
                              display={'flex'}
                              flexDirection={'column'}
                              gap={1.5}
                            >
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: '-26px',
                                  width: '2px',
                                  backgroundColor: (theme) =>
                                    alpha(timelineColor, 0.3),
                                  top: 0,
                                  bottom: isLast ? 'auto' : '-16px',
                                  height: isLast ? '22px' : 'auto',
                                }}
                              />

                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: '-24px',
                                  top: '20px',
                                  width: '24px',
                                  height: '2px',
                                  backgroundColor: (theme) =>
                                    alpha(timelineColor, 0.3),
                                  opacity: isSelected ? 1 : 0.6,
                                }}
                              />

                              <Box
                                display={'flex'}
                                gap={2}
                                justifyContent={'space-between'}
                              >
                                <Typography
                                  variant="body2"
                                  noWrap
                                  title={plan?.planName}
                                  color="text.primary"
                                >
                                  {plan?.planName ?? ''}
                                </Typography>
                                <Box
                                  display={'flex'}
                                  alignItems={'center'}
                                  gap={1}
                                >
                                  {isActive && (
                                    <Chip
                                      label={t('today_s_active_plan')}
                                      size="small"
                                      color="success"
                                      variant="filled"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              <Box>
                                <Box
                                  display={'flex'}
                                  alignItems={'center'}
                                  gap={1}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {plan?.startDateWithTime
                                      ? `Start: ${convertUtcToDisplayTime(
                                          plan.startDateWithTime,
                                          'configured',
                                          customerTz,
                                          DATE_TIME_FORMAT
                                        )}`
                                      : ''}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {plan?.endDateWithTime
                                      ? `End: ${convertUtcToDisplayTime(
                                          plan.endDateWithTime,
                                          'configured',
                                          customerTz,
                                          DATE_TIME_FORMAT
                                        )}`
                                      : ''}
                                  </Typography>
                                </Box>

                                <Box
                                  display={'flex'}
                                  gap={1}
                                  alignItems={'center'}
                                  mt={1}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{
                                      bgcolor: 'action.hover',
                                      px: 0.5,
                                      borderRadius: 0.5,
                                    }}
                                  >
                                    {`${plan.noOfAssets} Assets`}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display={'block'}
                                  >
                                    {`• ${plan.totalTonnage} tons (${blendString})`}
                                  </Typography>
                                </Box>
                              </Box>

                              <Box
                                display={'flex'}
                                gap={1}
                                alignItems={'center'}
                              >
                                <CustomLoadingButton
                                  onClick={() => {
                                    handleViewPlanDetails(plan);
                                  }}
                                  startIcon={<RemoveRedEye fontSize="small" />}
                                  size="small"
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  {t('grade_importer_section_details')}
                                </CustomLoadingButton>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          }
        )}
      </Box>

      {modalData.open && (
        <PreviewDateSelectorModal
          date={date}
          selectedDateRange={selectedDateRange}
          isOpen={modalData.open}
          onClose={handleClosePreviewDateSelectorModal}
          onApply={handleApply}
        />
      )}
    </Box>
  );
};
